import {
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  SECONDS_IN_MINUTE,
  ZERO_TIME_HHmm,
} from "../../constants";
import { TimeZoneEnum } from "../../enums";
import {
  IStationRouteComputedData,
  IStationRouteDto,
  IStationRouteResult,
  IStationRouteSaveRouteFields,
  IStationRouteStationInfo,
} from "../../models";
import { getInnerArray } from "../array.helpers";
import {
  buildCumulativeTimeArrivalsFromLegSeconds,
  getDayjsFromTimeString,
  isValidTimeString,
  resolveAppliedLegSeconds,
} from "../date.helpers";
import { Log } from "../log.helpers";
import { hasKeys } from "../objects.helpers";
import {
  ensureRoutingWaypointsAreInterior,
  isStationRouteWaypointStation,
} from "../routing-waypoint";
import {
  getStationRouteDurationsLegs,
  getStationRouteStationDuration,
} from "./station-route.helpers";

export const sortStationRouteStationsByTime = (
  stations: IStationRouteStationInfo[],
  timezone: TimeZoneEnum,
): IStationRouteStationInfo[] => {
  return stations.sort((a, b) => {
    if (
      !a.timeArrival ||
      !b.timeArrival ||
      !isValidTimeString(a.timeArrival) ||
      !isValidTimeString(b.timeArrival)
    ) {
      return 0;
    }

    const date1 = getDayjsFromTimeString(a.timeArrival, timezone).valueOf();
    const date2 = getDayjsFromTimeString(b.timeArrival, timezone).valueOf();

    return date1 - date2;
  });
};

export const normalizeStationRouteComputedData = (
  computedData: IStationRouteComputedData | undefined,
  reorderStationsByTime = false,
  timezone: TimeZoneEnum,
): IStationRouteComputedData => {
  if (!computedData?.stationInfos?.length) {
    return computedData || ({} as IStationRouteComputedData);
  }

  const sortedStations = ensureRoutingWaypointsAreInterior(
    reorderStationsByTime
      ? sortStationRouteStationsByTime(computedData.stationInfos, timezone)
      : computedData.stationInfos,
  );
  return {
    ...computedData,
    stationInfos: sortedStations,
  };
};

export const reorderStationRouteStationsByOrderedArray = (
  computedData: IStationRouteComputedData | undefined,
  stationNewOrderIds: number[],
  timezone: TimeZoneEnum,
): IStationRouteComputedData | undefined => {
  const { stationInfos: stationsInfo } = computedData || {};

  if (!stationsInfo?.length || !stationNewOrderIds?.length) {
    return computedData;
  }

  const stationsInfoMap = new Map(stationsInfo.map(s => [s.id, s]));
  const stationsInfoFiltered = stationNewOrderIds
    .map(x => stationsInfoMap.get(x))
    .filter(x => x !== undefined);

  const computedDataUpdated: IStationRouteComputedData = {
    ...computedData,
    stationInfos: stationsInfoFiltered,
  };

  const computedDataTransformed = normalizeStationRouteComputedData(
    computedDataUpdated,
    false,
    timezone,
  );
  return computedDataTransformed;
};

export const removeStationFromStationRouteDtoMetadata = (
  stationRouteDto: IStationRouteDto,
  stationsToRemoveIds: number[],
  timezone: TimeZoneEnum,
  reorderStationsByTime = false,
): IStationRouteComputedData => {
  const { computedData } = stationRouteDto;

  if (!computedData || !hasKeys(computedData) || !stationsToRemoveIds?.length) {
    return {};
  }

  const stationsToRemoveIdsSet = new Set(stationsToRemoveIds);
  const computedDataUpdated: IStationRouteComputedData = {
    ...computedData,
    stationInfos:
      computedData.stationInfos?.filter(
        x => !stationsToRemoveIdsSet.has(x.id),
      ) || [],
  };

  const computedDataTransformed = normalizeStationRouteComputedData(
    computedDataUpdated,
    reorderStationsByTime,
    timezone,
  );
  return computedDataTransformed;
};

/**
 * Seconds each leg (leg i connects station i -> i + 1) currently spans on the live HH:mm timeline,
 * `undefined` where a station has no usable arrival time. This is the timeline the editor shows and
 * saves, so it is what "the current plan" means — including unsaved manual edits.
 */
const getStationRouteCurrentLegSeconds = ({
  stations,
  timezone,
}: {
  stations: IStationRouteStationInfo[];
  timezone: TimeZoneEnum;
}): (number | undefined)[] =>
  stations.slice(1).map((_station, legIndex) => {
    const duration = getStationRouteStationDuration(
      stations,
      legIndex,
      timezone,
      "second",
    );

    return duration === undefined ? undefined : Math.max(duration, 0);
  });

/**
 * Planned seconds per leg for the measured-times preview, `undefined` where the plan is unknown.
 * The HH:mm timeline is the source of truth because the editor writes it directly; `result.durations`
 * is only trusted where it still agrees with that timeline to the minute, which is the resolution an ML
 * path is stored at. Anything else is a stale array left behind by a manual station time edit.
 */
export const getStationRoutePlannedLegSeconds = ({
  stationRouteDto,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
  timezone: TimeZoneEnum;
}): (number | undefined)[] => {
  const stations = stationRouteDto?.computedData?.stationInfos || [];

  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION) {
    return [];
  }

  const currentLegSeconds = getStationRouteCurrentLegSeconds({
    stations,
    timezone,
  });
  const durations = stationRouteDto?.result?.durations;

  if (durations?.length !== currentLegSeconds.length) {
    return currentLegSeconds;
  }

  return currentLegSeconds.map((currentSeconds, legIndex) => {
    const duration = Math.max(durations[legIndex] || 0, 0);
    const isDurationInSyncWithTimeline =
      currentSeconds !== undefined &&
      Math.abs(duration - currentSeconds) < SECONDS_IN_MINUTE;

    return isDurationInSyncWithTimeline ? duration : currentSeconds;
  });
};

/**
 * Bulk sibling of {@link updateStationRouteComputedDataStationsTime}: rewrites the arrival time of EVERY
 * station from a per-leg seconds array instead of moving a single one. The editor keeps the ML path as an
 * HH:mm timeline anchored on the first station and derives `result.durations` from it on save
 * (see {@link buildStationRouteSaveRouteFields}), so re-timing the stations is what applies the legs.
 * That round trip is minute-quantised and the legs are NOT altered to hide it: each station lands on the
 * nearest minute of the running total, which keeps the route total within half a minute of the applied
 * one. The ML-path flow tells the user its times are stored at whole-minute resolution.
 * Legs the user did not pick keep the delta they currently have rather than being rebuilt from a stale
 * plan, and stations beyond the end of `legSeconds` keep the time they already had.
 */
export const updateStationRouteComputedDataStationsTimeBulk = ({
  stationRouteDto,
  legSeconds,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto>;
  /** One entry per leg; `undefined` means "the user picked nothing here, keep the current delta". */
  legSeconds: (number | undefined)[];
  timezone: TimeZoneEnum;
}): IStationRouteComputedData => {
  const stations = stationRouteDto?.computedData?.stationInfos || [];
  const anchorTime = stations.at(0)?.timeArrival || ZERO_TIME_HHmm;
  const timeArrivals = buildCumulativeTimeArrivalsFromLegSeconds(
    resolveAppliedLegSeconds({
      appliedLegSeconds: legSeconds,
      currentLegSeconds: getStationRouteCurrentLegSeconds({
        stations,
        timezone,
      }),
    }),
    anchorTime,
  );

  const stationsUpdated: IStationRouteStationInfo[] = stations.map(
    (station, index) => ({
      ...station,
      timeArrival: timeArrivals[index] ?? station.timeArrival,
    }),
  );

  const computedDataToUpdate: IStationRouteComputedData = {
    ...(stationRouteDto.computedData || {}),
    stationInfos: stationsUpdated,
  };
  Log.debug(
    "Station route computed data after bulk station time update:",
    computedDataToUpdate,
  );
  return computedDataToUpdate;
};

/**
 * Build the persisted route fields for a station route from its (possibly re-timed) station list.
 * The stations are re-ordered by arrival time first — so changing a station's time moves it to the
 * matching position in the list — and orderedStationIds / waypointIds / durations plus the origin &
 * destination station ids are all derived from that time-sorted order. This mirrors the way courses
 * are sorted by arrival time on save, which station routes previously did not do.
 */
export const buildStationRouteSaveRouteFields = ({
  stationInfos,
  timezone,
  existingResult,
}: {
  stationInfos: IStationRouteStationInfo[] | undefined;
  timezone: TimeZoneEnum;
  existingResult: IStationRouteResult | undefined;
}): IStationRouteSaveRouteFields => {
  const sortedStations = sortStationRouteStationsByTime(
    [...(stationInfos || [])],
    timezone,
  );
  const orderedStationIds = sortedStations.map(({ id }) => id);
  const realStations = sortedStations.filter(
    station => !isStationRouteWaypointStation(station),
  );
  const waypointStops = sortedStations.filter(isStationRouteWaypointStation);

  if (!orderedStationIds.length) {
    const routeFields: IStationRouteSaveRouteFields = {
      orderedStationIds,
      waypointIds: [],
      result: existingResult,
    };
    return routeFields;
  }

  const durations = getStationRouteDurationsLegs(sortedStations, timezone);
  const totalDuration = durations.reduce((sum, duration) => sum + duration, 0);

  const realOrderedIds = realStations.map(({ id }) => id);

  const routeFields: IStationRouteSaveRouteFields = {
    orderedStationIds,
    waypointIds: getInnerArray(realOrderedIds),
    originStationId: realOrderedIds[0],
    destinationStationId: realOrderedIds[realOrderedIds.length - 1],
    result: {
      distances: [],
      totalDistance: 0,
      ...existingResult,
      orderedStationIds,
      waypointStops: waypointStops.length ? waypointStops : undefined,
      totalDuration,
      durations,
    },
  };
  return routeFields;
};
